import React, { Suspense, lazy } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { AppLayout } from "@/components/AppLayout";
import { RouteTracker } from "@/components/RouteTracker";
import { Toaster } from "@/components/ui/sonner";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import { TooltipProvider } from "@/components/ui/tooltip";
import ForumsPage from "@/pages/ForumsPage";
import SignalsPage from "@/pages/SignalsPage";
import NewsPage from "@/pages/NewsPage";
import OnboardingPage from "@/pages/OnboardingPage";
import AccountPage from "@/pages/AccountPage";
import AdminPage from "./pages/AdminPage";
import ThreadsPage from "@/pages/ThreadsPage";
import ThreadDetailPage from "@/pages/ThreadDetailPage";
import UsersRouter from "@/pages/UsersRouter";
import FollowsPage from "@/pages/FollowsPage";
import AlertsPage from "@/pages/AlertsPage";
import AuthPage from "@/pages/AuthPage";

// Lazy-loaded : StatsPage pulls recharts (~100 kB gz) that nothing else needs
// — keep it out of the common bundle.
const StatsPage = lazy(() => import("@/pages/StatsPage"));

import "@/styles/globals.css";

const qc = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, staleTime: 15_000, retry: 1 },
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={qc}>
      {/* delayDuration 200 → tooltips feel snappy without firing on accidental hovers */}
      <TooltipProvider delayDuration={200}>
      <BrowserRouter>
        <RouteTracker />
        <Routes>
          <Route path="/login" element={<AuthPage />} />
          {/* Post-signup wizard — full-screen, no sidebar. Self-redirects to
              /forums when onboarding is already completed. */}
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route element={<AppLayout />}>
            <Route path="/" element={<Navigate to="/forums" replace />} />
            {/* Forums : /forums (= all) or /forums/:cat (hot, followed…). */}
            <Route path="/forums" element={<ForumsPage />} />
            <Route path="/forums/:cat" element={<ForumsPage />} />
            {/* Activity→price burst radar. */}
            <Route path="/signals" element={<SignalsPage />} />
            {/* Aggregated cross-forum news feed (the "radar" tab). */}
            <Route path="/news" element={<NewsPage />} />
            {/* Detail of a single forum + sub-tab (threads, news, history). */}
            <Route path="/forums/:id/threads" element={<ThreadsPage />} />
            <Route path="/forums/:id/threads/:tab" element={<ThreadsPage />} />
            <Route path="/threads/:id" element={<ThreadDetailPage />} />
            <Route path="/threads/:id/:order" element={<ThreadDetailPage />} />
            {/* Users : /users dispatches to list or detail depending on the segment. */}
            <Route path="/users" element={<UsersRouter />} />
            <Route path="/users/:segment" element={<UsersRouter />} />
            {/* Follows : /follows + /follows/:tab. */}
            <Route path="/follows" element={<FollowsPage />} />
            <Route path="/follows/:tab" element={<FollowsPage />} />
            <Route path="/alerts" element={<AlertsPage />} />
            <Route
              path="/stats"
              element={
                <Suspense fallback={<div className="p-6 text-sm text-foreground-muted">Chargement…</div>}>
                  <StatsPage />
                </Suspense>
              }
            />
            <Route path="/account" element={<AccountPage />} />
            {/* Admin dashboard (signups). Server-side gated by ADMIN_EMAILS. */}
            <Route path="/admin" element={<AdminPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster />
      <UpgradeDialog />
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      </TooltipProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
